import React, { useState, useEffect } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { useNavigate, Link } from 'react-router-dom';
import Footer from "./Footer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ZMCMConveyanceDetails = () => {
    const [profile, setProfile] = useState(null);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [visitRows, setVisitRows] = useState([]);
    const [selectedMonthType, setSelectedMonthType] = useState("current");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const navigate = useNavigate();
    const { min, max } = getMonthRange();
    const currentMonth = new Date().toLocaleString("en-US", { month: "long" });
    // Load profile first
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem("authToken");
                const res = await fetch(`${API_BASE_URL}profile`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });

                const result = await res.json();
                if (result.status && result.profile) {
                    setProfile(result.profile);
                    fetchDetails(result.profile.emp_code);
                } else {
                    setMessage("Unable to load profile");
                    setLoading(false);
                }
            } catch (error) {
                setMessage("Failed to load profile");
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    // Fetch details
    const fetchDetails = async (empCode) => {
        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${API_BASE_URL}getcmzmdetails/${empCode}`, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
            });

            const result = await response.json();

            if (result.status && result.data.status === "success") {
                const row = result.data.data[0];
                setDetails([row]);

                const branchArray = row.branches_list || [];
                const role = row.role;

                // 🔹 Decide visits per branch based on role
                const visitsPerBranch =
                    role === "cluster_manager" ? 2 : 1;

                // 🔹 Total visits
                const totalVisits = branchArray.length * visitsPerBranch;

                // 🔹 Amount split
                const totalAmount = parseFloat(row.amount);
                setTotalAmount(totalAmount);
                const perVisitAmount = totalAmount / totalVisits;
                const displayAmount = Number(perVisitAmount).toFixed(2);
                const visits = [];

                branchArray.forEach((b) => {
                    for (let visitNo = 1; visitNo <= visitsPerBranch; visitNo++) {
                        visits.push({
                            visit_no: visitNo,
                            branch_id: b.id,
                            branch_name: b.name,
                            date: "",
                            time: "",
                            day: "",
                            purpose: "",
                            remarks: "",
                            amount: displayAmount,
                            attachment: null,
                        });
                    }
                });

                setVisitRows(visits);
            } else {
                setMessage(result.data?.message || "No conveyance records found");
            }
        } catch (err) {
            setMessage("Failed to load conveyance details");
        } finally {
            setLoading(false);
        }
    };

    // Date & Day auto-fill
    const handleDateChange = (index, value) => {
        const updated = [...visitRows];
        updated[index].date = value;

        const d = new Date(value);
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        updated[index].day = days[d.getDay()];

        setVisitRows(updated);
    };

    const handleTimeChange = (index, value) => {
        const updated = [...visitRows];
        updated[index].time = value;
        setVisitRows(updated);
    };

    // Purpose / Remarks update
    const handleInputChange = (index, field, value) => {
        const updated = [...visitRows];
        updated[index][field] = value;
        setVisitRows(updated);
    };

    const handleFileChange = (index, file) => {
        const updated = [...visitRows];
        updated[index].attachment = file;
        setVisitRows(updated);

    };
    const getSelectedMonthYear = () => {
        const now = new Date();

        const baseDate =
            selectedMonthType === "current"
                ? now
                : new Date(now.getFullYear(), now.getMonth() - 1, 1);

        const month = String(baseDate.getMonth() + 1).padStart(2, "0"); // MM
        const year = baseDate.getFullYear(); // YYYY

        return `${month}-${year}`; // eg: 12-2025
    };

    const handleSubmit = async (actionType) => {
        if (!profile) {
            alert("Profile not loaded");
            return;
        }

        const selectedRows = visitRows.filter(v => v.date);

        if (selectedRows.length === 0) {
            alert("Please select at least one visit date");
            return;
        }

        for (let i = 0; i < selectedRows.length; i++) {
            const v = selectedRows[i];

            if (!v.purpose.trim()) {
                alert(`Please enter Purpose for Visit ${v.visit_no} - ${v.branch_name}`);
                return;
            }

            if (!v.amount || v.amount <= 0) {
                alert(`Invalid amount for Visit ${v.visit_no} - ${v.branch_name}`);
                return;
            }
        }

        const formData = new FormData();
        const monthYear = getSelectedMonthYear();
        formData.append("emp_code", profile.emp_code);
        formData.append("employee_name", profile.comp_name);
        formData.append("role", details[0]?.role || "");
        formData.append("duration", details[0]?.duration_type || "");
        formData.append("visit_count", selectedRows.length);
        formData.append("submission_type", actionType);
        formData.append("totalAmount", totalAmount);
        formData.append("month_year", getSelectedMonthYear());

        selectedRows.forEach((v, i) => {
            formData.append(`visits[${i}][visit_no]`, v.visit_no);
            formData.append(`visits[${i}][branch_id]`, v.branch_id);
            formData.append(`visits[${i}][branch_name]`, v.branch_name);
            formData.append(`visits[${i}][date]`, v.date);
            formData.append(`visits[${i}][time]`, v.time);
            formData.append(`visits[${i}][day]`, v.day);
            formData.append(`visits[${i}][purpose]`, v.purpose);
            formData.append(`visits[${i}][remarks]`, v.remarks);
            formData.append(`visits[${i}][amount]`, v.amount);


            if (v.attachment) {
                formData.append(`visits[${i}][attachment]`, v.attachment);
            }
        });

        try {
            const token = localStorage.getItem("authToken");

            const res = await fetch(`${API_BASE_URL}save_zmcm_conveyance`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            const result = await res.json();

            if (result.status === true || result.status === "success") {
                alert("✅ Conveyance submitted successfully!");
                setIsSubmitted(true);
                navigate(`/manage_cmzmconveyance`);
            } else {
                alert(result.message || "❌ Submission failed");
            }
        } catch (err) {
            console.error(err);
            alert("❌ Server error while submitting");
        }
    };




    // Get first & last date of allowed month
    function getMonthRange() {
        const now = new Date();
        const baseDate =
            selectedMonthType === "current"
                ? now
                : new Date(now.getFullYear(), now.getMonth() - 1, 1);

        const firstDay = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
        const lastDay = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);

        return {
            min: firstDay.toISOString().split("T")[0],
            max: lastDay.toISOString().split("T")[0],
        };
    }

    const getVisitBg = (visitNo) => {
        if (visitNo === 1) {
            return {
                background: "#f0f6ff",   // light blue
                border: "1px solid #cfe0ff",
            };
        }
        if (visitNo === 2) {
            return {
                background: "#f1fbf4",   // light green
                border: "1px solid #cdebd6",
            };
        }
        return {
            background: "#fff",
            border: "1px solid #e3ecf7",
        };
    };


    if (loading) {
        return <h3 style={{ padding: 20, color: "#6ca0dc" }}>Loading...</h3>;
    }

    return (
        <div className="hold-transition sidebar-mini layout-fixed"
            style={{ background: "#f4f7fb" }}>
            <div className="wrapper">

                <Header />
                <Sidebar />

                <div className="content-wrapper" style={{ padding: "25px" }}>

                    {/* PAGE TITLE */}
                    <h2 style={{
                        color: "#6ca0dc",
                        fontWeight: "800",
                        fontSize: "24px",
                        marginBottom: "12px"
                    }}>
                        ZM / CM Conveyance Details
                    </h2>

                    {/* MESSAGE */}
                    {message && (
                        <div style={{
                            padding: "12px",
                            background: "#fdeeee",
                            borderLeft: "4px solid #d87a7a",
                            borderRadius: "8px",
                            marginBottom: "18px",
                            color: "#8b3f3f",
                        }}>
                            {message}
                        </div>
                    )}

                    {/* PROFILE CARD */}
                    {profile && (
                        <div
                            style={{
                                background: "#ffffffee",
                                padding: "15px 20px",
                                borderRadius: "12px",
                                border: "1px solid #e2e8f5",
                                marginBottom: "20px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                            }}
                        >
                            <b style={{ color: "#557a95" }}>Employee:</b>{" "}
                            {profile.comp_name} ({profile.emp_code})
                        </div>
                    )}

                    {/* MASTER SUMMARY */}
                    {/* <h3 style={{ color: "#557a95", marginBottom: "10px" }}>Master Summary</h3> */}

                    {details.length > 0 && (
                        <div style={{
                            background: "#ffffffee",
                            padding: "20px",
                            borderRadius: "12px",
                            border: "1px solid #d9e3f1",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                            marginBottom: "25px"
                        }}>
                            {details.map((row, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "22px",
                                        fontSize: "14px",
                                        color: "#425b70",
                                    }}
                                >
                                    <span><b>ID:</b> {row.id}</span>
                                    <span><b>Role:</b> {row.role}</span>
                                    <span><b>Duration:</b> {row.duration_type} days</span>
                                    {/* <span><b>1–15:</b> {row["1_15_cnt"]}</span>
                                    <span><b>16–30:</b> {row["16_30_cnt"]}</span> */}
                                    <span><b>Total Branches:</b> {row["1_30_cnt"]}</span>
                                    <span><b>Amount:</b> ₹{row.amount}</span>
                                    <span><b>Branches:</b> {row.branches}</span>
                                    <span><b>Created:</b> {row.created_on}</span>

                                    <div style={{ marginBottom: "15px", display: "flex", gap: "20px" }}>
                                        <label>
                                            <input
                                                type="radio"
                                                value="current"
                                                checked={selectedMonthType === "current"}
                                                onChange={() => setSelectedMonthType("current")}
                                            />{" "}
                                            Current Month
                                        </label>

                                        <label>
                                            <input
                                                type="radio"
                                                value="previous"
                                                checked={selectedMonthType === "previous"}
                                                onChange={() => setSelectedMonthType("previous")}
                                            />{" "}
                                            Previous Month
                                        </label>
                                    </div>
                                    <span
                                        style={{
                                            background: "#ffefc7",
                                            color: "#a06400",
                                            padding: "4px 10px",
                                            borderRadius: "8px",
                                            fontWeight: "700",
                                            boxShadow: "0 1px 4px rgba(0,0,0,0.15)"
                                        }}
                                    >
                                        <b>Current Month:</b>
                                        {currentMonth}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* VISIT RECORDS */}
                    <h3 style={{ color: "#557a95", fontSize: "15px", marginBottom: "8px" }}>
                        Visit Records ({visitRows.length} Visits)
                    </h3>

                    <div
                        style={{
                            maxHeight: "500px",
                            overflowY: "auto",
                            background: "#ffffffee",
                            borderRadius: "10px",
                            border: "1px solid #d9e3f1",
                            padding: "10px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        }}
                    >
                        {visitRows.map((row, index) => (
                            <div
                                key={index}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: "12px",
                                    padding: "8px 10px",
                                    marginBottom: "8px",
                                    borderRadius: "8px",
                                    fontSize: "12.5px",
                                    ...getVisitBg(row.visit_no), // 🎨 color by V1 / V2
                                }}
                            >
                                {/* Visit No */}
                                <span style={{ width: "60px" }}>
                                    <b>V:</b> {row.visit_no}
                                </span>

                                {/* Branch ID */}
                                <span style={{ width: "70px" }}>
                                    <b>ID:</b> {row.branch_id}
                                </span>

                                {/* Branch Name */}
                                <span style={{ width: "180px", whiteSpace: "nowrap" }}>
                                    <b>Branch:</b> {row.branch_name}
                                </span>



                                {/* Date */}
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <b style={{ marginRight: "4px" }}>Date:</b>
                                    <input
                                        type="date"
                                        value={row.date}
                                        min={min}
                                        max={max}
                                        onChange={(e) => handleDateChange(index, e.target.value)}
                                        style={{
                                            fontSize: "12px",
                                            padding: "3px",
                                            height: "26px",
                                            borderRadius: "4px",
                                            border: "1px solid #9ad1d4",
                                        }}
                                    />
                                </div>

                                {/* Day */}
                                <span style={{ width: "65px" }}>
                                    <b>Day:</b> {row.day}
                                </span>

                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <b style={{ marginRight: "4px" }}>Time:</b>
                                    <input
                                        type="time"
                                        value={row.time}
                                        onChange={(e) => handleTimeChange(index, e.target.value)}
                                        style={{
                                            fontSize: "12px",
                                            padding: "3px",
                                            height: "26px",
                                            borderRadius: "4px",
                                            border: "1px solid #9ad1d4",
                                        }}
                                    />
                                </div>

                                {/* Purpose */}
                                <input
                                    type="text"
                                    placeholder="Purpose"
                                    value={row.purpose}
                                    onChange={(e) => handleInputChange(index, "purpose", e.target.value)}
                                    style={{
                                        width: "240px",
                                        fontSize: "12px",
                                        padding: "4px",
                                        height: "26px",
                                        borderRadius: "4px",
                                        border: "1px solid #9ad1d4",
                                    }}
                                />

                                {/* Remarks */}
                                <input
                                    type="text"
                                    placeholder="Remarks"
                                    value={row.remarks}
                                    onChange={(e) => handleInputChange(index, "remarks", e.target.value)}
                                    style={{
                                        width: "240px",
                                        fontSize: "12px",
                                        padding: "4px",
                                        height: "26px",
                                        borderRadius: "4px",
                                        border: "1px solid #9ad1d4",
                                    }}
                                />

                                {/* Amount */}
                                <span
                                    style={{
                                        background: "#6ca0dc",
                                        color: "#fff",
                                        padding: "3px 10px",
                                        borderRadius: "14px",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                    }}
                                >
                                    ₹{row.amount}
                                </span>

                                {/* Attachment */}
                                <input
                                    type="file"
                                    accept="image/*,application/pdf"
                                    onChange={(e) => handleFileChange(index, e.target.files[0])}
                                    style={{
                                        fontSize: "11px",
                                    }}
                                />
                            </div>
                        ))}
                    </div>

                    <div
                        style={{
                            textAlign: "center",
                            marginTop: "20px",
                            display: "flex",
                            justifyContent: "center",
                            gap: "16px",
                        }}
                    >
                        {/* Submit Conveyance */}
                        <button
                            onClick={() => handleSubmit("draft")}
                            style={{
                                background: "linear-gradient(135deg, #5a8dee, #6ca0dc)",
                                color: "#fff",
                                padding: "12px 36px",
                                fontSize: "15px",
                                border: "none",
                                borderRadius: "10px",
                                cursor: "pointer",
                                fontWeight: "700",
                                boxShadow: "0 4px 12px rgba(90,141,238,0.4)",
                            }}
                        >
                            💾 Save as Drafts
                        </button>

                        {/* Send for Approval */}
                        <button
                            onClick={() => handleSubmit("approval")}
                            style={{
                                background: "linear-gradient(135deg, #34a853, #4caf50)",
                                color: "#fff",
                                padding: "12px 36px",
                                fontSize: "15px",
                                border: "none",
                                borderRadius: "10px",
                                cursor: "pointer",
                                fontWeight: "700",
                                boxShadow: "0 4px 12px rgba(52,168,83,0.4)",
                            }}
                        >
                            🚀 Send for Approval
                        </button>
                    </div>


                </div>

                <Footer />
            </div>
        </div>
    );
};

export default ZMCMConveyanceDetails;
